import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type {
	EditorInsertContentCommandProperties,
	EditorInsertContentCommandResult,
} from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';

import { CommandHandler, type PassThroughCommandProps } from './command-handler';

/**
 * Handles editor insert-content requests by delegating to the host-provided
 * `onInsertContent` callback, which wires the actual insert/replace logic into the editor.
 */
export class InsertContentCommandHandler extends CommandHandler<
	EditorInsertContentCommandProperties,
	EditorInsertContentCommandResult
> {
	constructor(props: PassThroughCommandProps) {
		super(props);
	}

	execute(
		payload: EditorInsertContentCommandProperties,
	): Promise<EditorInsertContentCommandResult> | EditorInsertContentCommandResult {
		const { editorAPI, onInsertContent, onUploadAndInsertMedia } = this.props;
		if (!editorAPI) {
			return;
		}
		let mediaInsertionResult: Promise<EditorInsertContentCommandResult> | undefined;
		let didProduceAdfInsertion = false;
		const requireInsertionAcknowledgement = payload.requireInsertionAcknowledgement === true;

		const shouldInsertMediaAtStoredSelection =
			payload.mode === 'upload-and-insert-media' &&
			payload.useStoredSelection &&
			payload.interactionSource === 'remixModalFromMediaInserts';

		// Capture the saved selection before entering the transaction
		// callback — so it survives any state changes triggered by closing the
		// ephemeral preview (which clears savedSelection via stopRemixThinking).
		const savedSelection =
			payload.useStoredSelection &&
			(payload.placement === 'after-block' ||
				shouldInsertMediaAtStoredSelection ||
				(payload.placement === 'replace-selection' && fg('platform_editor_ai_remix_toolbar')))
				? editorAPI?.malleableUi?.sharedState?.currentState()?.savedSelection
				: undefined;

		editorAPI.core.actions.execute(({ tr }) => {
			let start = tr.selection.from,
				end = tr.selection.to;

			// Page-level remix: append content at the very end of the document,
			// regardless of the current cursor position.
			if (payload.placement === 'end-of-doc') {
				start = end = tr.doc.content.size - 1;
			} else if (
				payload.placement === 'after-block' ||
				(payload.placement === 'replace-selection' && fg('platform_editor_ai_remix_toolbar'))
			) {
				if (savedSelection) {
					const { from, to, anchorCellPos, headCellPos } = savedSelection;
					start = Math.min(
						from,
						anchorCellPos ?? Number.MAX_VALUE,
						headCellPos ?? Number.MAX_VALUE,
					);
					end = Math.max(to, anchorCellPos ?? 0, headCellPos ?? 0);
				}

				if (payload.placement === 'after-block' && !shouldInsertMediaAtStoredSelection) {
					const sharedDepth = tr.doc.resolve(start).sharedDepth(end);
					if (sharedDepth > 0) {
						start = end = tr.doc.resolve(end).after(1);
					}
				}
			}

			// upload-and-insert-media is async and handled separately via the React-layer callback
			// we are only setting the selection here.
			if (payload.mode === 'upload-and-insert-media' && onUploadAndInsertMedia) {
				const positions: [number, number] =
					payload.placement === 'replace-selection' || shouldInsertMediaAtStoredSelection
						? [start, end]
						: [end, end];
				mediaInsertionResult = onUploadAndInsertMedia(
					payload.data.url,
					payload.remixSubtype ? { dynamicUiSubtype: payload.remixSubtype } : undefined,
					payload.data.presentation,
					{
						conversationChannelId: payload.conversationChannelId,
						isAIGenerated: payload.isAIGenerated,
						inputMethod:
							payload.interactionSource === 'remixModalFromMediaInserts'
								? INPUT_METHOD.REMIX_FROM_MEDIA_INSERTS
								: INPUT_METHOD.REMIX,
						aspectRatio: payload.aspectRatio,
						positions:
							fg('platform_editor_ai_remix_toolbar') || shouldInsertMediaAtStoredSelection
								? positions
								: undefined,
						...(requireInsertionAcknowledgement ? { requireInsertionAcknowledgement: true } : {}),
					},
				).then((result) => {
					if (result?.type === 'create' && result?.fieldId) {
						// The upload+insert lands after the remix reports ready, so the thinking-widget
						// anchor no longer marks the result. Record the media id for the remix ready
						// bar's "View" action to scroll to.
						const setRemixImageMediaId = editorAPI.malleableUi?.commands.setRemixImageMediaId;
						if (setRemixImageMediaId) {
							editorAPI.core.actions.execute(setRemixImageMediaId({ mediaId: result.fieldId }));
						}
					}
					if (!fg('platform_editor_ai_remix_toolbar')) {
						return result?.fieldId
							? requireInsertionAcknowledgement
								? { inserted: true as const, mediaFileId: result.fieldId }
								: { mediaFileId: result.fieldId }
							: undefined;
					}
					if (!result || !payload.conversationChannelId) {
						return result?.fieldId
							? requireInsertionAcknowledgement
								? { inserted: true as const, mediaFileId: result.fieldId }
								: { mediaFileId: result.fieldId }
							: undefined;
					}
					const { fieldId, type } = result;
					if (type === 'create') {
						editorAPI.core.actions.execute(
							editorAPI.malleableUi?.commands.updateRemixImageHistoryFieldId({
								conversationId: payload.conversationChannelId,
								fieldId: fieldId,
							}),
						);
					}
					return requireInsertionAcknowledgement
						? { inserted: true as const, mediaFileId: result.fieldId }
						: { mediaFileId: result.fieldId };
				});
				const selection = TextSelection.create(tr.doc, end);
				return selection ? tr.setSelection(selection) : tr;
			}

			const insertionTransaction =
				editorAPI.aiExperience?.commands.insertContent(payload, [start, end])?.({ tr }) ?? null;
			didProduceAdfInsertion = insertionTransaction !== null;
			return insertionTransaction;
		});

		onInsertContent?.(payload);
		if (payload.mode === 'upload-and-insert-media') {
			return mediaInsertionResult;
		}
		return requireInsertionAcknowledgement && didProduceAdfInsertion
			? { inserted: true }
			: undefined;
	}
}
